import { useCallback, useMemo, useState } from 'react';
import { Combobox, Icon, LegacyStack, Listbox, Tag, Text, VerticalStack } from '@shopify/polaris';
import { SearchMinor } from '@shopify/polaris-icons';

export const Multiselector = ({ deposits, title, selectedOptions: selectedOptionsProp, onChange }) => {
  const [selectedOptions, setSelectedOptions] = useState(selectedOptionsProp || []);
  const [inputValue, setInputValue] = useState('');

  const escapeSpecialRegExCharacters = useCallback((value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), []);

  const deselectedOptions = useMemo(
    () =>
      deposits.map((deposit) => ({
        id: deposit.id,
        name: deposit.name,
        main: deposit.main,
        isOnlyGroup: deposit.isOnlyGroup,
      })),
    [deposits],
  );
  const [options, setOptions] = useState(deselectedOptions);

  const updateText = useCallback(
    (value) => {
      setInputValue(value);

      if (value === '') {
        setOptions(deselectedOptions);
        return;
      }

      const filterRegex = new RegExp(escapeSpecialRegExCharacters(value), 'i');
      const resultOptions = deselectedOptions.filter((option) => option.name.match(filterRegex));
      setOptions(resultOptions);
    },
    [deselectedOptions, escapeSpecialRegExCharacters],
  );

  const optionsMarkup =
    options.length > 0
      ? options.map((option) => {
          const { id, name, main, isOnlyGroup } = option;

          return (
            <Listbox.Option
              key={`${id}`}
              value={id}
              disabled={main}
              selected={selectedOptions.includes(id)}
              accessibilityLabel={name}
            >
              {isOnlyGroup ? `${name} (Grupo)` : name}
            </Listbox.Option>
          );
        })
      : null;

  const updateSelection = useCallback(
    (selected) => {
      if (selectedOptions.includes(selected)) {
        setSelectedOptions(selectedOptions.filter((option) => option !== selected));
        onChange(selectedOptions.filter((option) => option !== selected));
      } else {
        setSelectedOptions([...selectedOptions, selected]);
        onChange([...selectedOptions, selected]);
      }

      updateText('');
    },
    [selectedOptions, updateText, onChange],
  );

  const removeTag = useCallback(
    (tag) => () => updateSelection(tag),
    [updateSelection],
  );

  const tagsMarkup = selectedOptions.map((option) => {
    const deposit = deposits.find((d) => d.id === option);
    const label = deposit?.isOnlyGroup ? `${deposit.name} (Grupo)` : (deposit?.name || option);
    return (
      <LegacyStack key={`option-${option}`}>
        <Tag onRemove={removeTag(option)}>{label}</Tag>
      </LegacyStack>
    );
  });

  return (
    deposits && (
      <div style={{ height: '225px' }}>
        <VerticalStack gap="2">
          <Text variant="bodyMd" as="p" fontWeight="regular">
            {title || 'Depósitos asignados'}
          </Text>
          <Combobox
            allowMultiple
            label="Selecciona depósitos"
            activator={
              <Combobox.TextField
                prefix={<Icon source={SearchMinor} />}
                onChange={updateText}
                label="Buscar depósitos"
                labelHidden
                value={inputValue}
                placeholder="Buscar depósitos"
                autoComplete="off"
              />
            }
          >
            {optionsMarkup ? <Listbox onSelect={updateSelection}>{optionsMarkup}</Listbox> : null}
          </Combobox>
          <div style={{ paddingLeft: '8px', paddingRight: '8px', paddingTop: '12px' }}>
            <VerticalStack gap="2">
              {selectedOptions.length === 0 && (
                <Text variant="bodyMd" as="p" fontWeight="regular" color="subdued">
                  No hay depósitos seleccionados
                </Text>
              )}
              {selectedOptions.length > 0 && tagsMarkup}
            </VerticalStack>
          </div>
        </VerticalStack>
      </div>
    )
  );
};
